import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  RiCalendarLine,
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiArrowRightLine,
  RiTimeLine,
  RiTeamLine,
  RiMapPinLine,
  RiPlaneLine,
  RiBuilding2Line,
  RiArrowDownSLine,
  RiAddLine,
  RiCloseLine,
  RiArrowUpSLine,
  RiUserAddLine,
  RiEyeLine,
  RiEditLine,
  RiCheckboxCircleLine,
  RiSuitcase2Line,
  RiNurseLine,
  RiServiceBellLine
} from "@remixicon/react";
import { Skeleton } from "@/components/ui/Skeleton";
import { useAuthStore } from "@store/authStore";
import { apiClient } from "@/lib/api";
import { getAircraftModelName } from "@/features/tails/utils";
import { useAirports } from "@/features/quotes/services/airportService";
import AddServiceModal from "./AddServiceModal";
import AddPassengerDrawer from "./AddPassengerDrawer";
import AddCrewModal from "./AddCrewModal";
import TripSheetPreviewDrawer from "./TripSheetPreviewDrawer";
import { getAirportTimezone } from '@/utils/airportTimezone';
import { formatUTCToDateTime } from '@/utils';

const Section = ({ icon: Icon, title, children, isOpen, onToggle, completed, locked }) => {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border ${locked ? "border-gray-100 opacity-60" : "border-gray-200"}`}>
      <button
        type="button"
        onClick={locked ? undefined : onToggle}
        className={`w-full flex items-center justify-between p-6 text-left transition-colors ${locked ? "cursor-not-allowed" : ""}`}
      >
        <div className="flex items-center gap-3 flex-1">
          {Icon && <Icon className={`w-5 h-5 flex-shrink-0 ${locked ? "text-gray-400" : "text-gray-700"}`} />}
          <h3 className={`text-base ${locked ? "text-gray-400" : "text-gray-900"}`}>{title}</h3>
        </div>
        {completed && (
          <span className="flex items-center justify-center w-6 h-6 rounded-full">
            <RiCheckboxCircleLine className="w-6 h-6 text-primary-500" />
          </span>
        )}
      </button>
      {isOpen && !locked && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
};

const TripSheetPage = () => {
  const { userProfile } = useAuthStore();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quoteid");

  const { data: quoteData, isLoading: isQuoteLoading } = useQuery({
    queryKey: ["quoteDetails", quoteId],
    queryFn: () => apiClient.getQuoteById(quoteId),
    enabled: !!quoteId,
    staleTime: 0,
    refetchOnMount: "always",
    retry: 2,
    retryDelay: 1000,
    select: (data) => {
      let acceptedBid = null;
      let acceptedOption = null;
      for (const bidItem of data?.bid_items || []) {
        const bid = bidItem.bid;
        if (!bid?.bid_options) continue;
        const option = bid.bid_options.find((opt) => opt.status === "ACCEPTED");
        if (option) {
          acceptedBid = bid;
          acceptedOption = option;
          break;
        }
      }
      return { ...data, acceptedBid, acceptedOption };
    },
  });

  const acceptedBid = quoteData?.acceptedBid ?? null;
  const acceptedOption = quoteData?.acceptedOption ?? null;

  const aircraftId = acceptedOption?.aircraft?.aircraft_id;
  const { data: aircraftDetails, isLoading: isAircraftLoading } = useQuery({
    queryKey: ["aircraftDetails", aircraftId],
    queryFn: () =>
      apiClient.getAircraftById(aircraftId, { include: "make_model, features" }),
    enabled: !!aircraftId,
    staleTime: 0,
    refetchOnMount: "always",
    retry: 2,
    retryDelay: 1000,
  });

  const { data: organization } = useQuery({
    queryKey: ["organization", userProfile?.org_id],
    queryFn: () => apiClient.getOrganizationById(userProfile.org_id),
    enabled: !!userProfile?.org_id,
  });

  const { data: airportsData } = useAirports();

  const findAirport = useCallback(
    (icao) => {
      if (!airportsData || !icao) return null;
      const normalizedIcao = icao.toLowerCase();
      return airportsData.find((a) => a.icao?.toLowerCase() === normalizedIcao) || null;
    },
    [airportsData]
  );

  // Build derived data
  const clientName = useMemo(() => {
    return [quoteData?.client?.first_name, quoteData?.client?.last_name]
      .filter(Boolean)
      .join(" ");
  }, [quoteData]);

  const clientCompany = quoteData?.client?.company_name || "";
  const tailNumber = aircraftDetails?.tail_number || "";
  const aircraftName = aircraftDetails ? getAircraftModelName(aircraftDetails) : "";
  const passengerCount = acceptedBid?.passenger_count || 0;

  const departureAirport = useMemo(() => {
    const icao = acceptedBid?.departure_airport;
    const airport = findAirport(icao);
    return airport ? `${airport.name} (${icao})` : icao || "";
  }, [acceptedBid, findAirport]);

  const flights = useMemo(() => {
    if (!acceptedBid) return [];

    const buildFlight = (dep, arr, date) => ({
      pax: acceptedBid.passenger_count || 1,
      departure_date: date,
      departure_airport: dep || "",
      arrival_airport: arr || "",
    });

    if (acceptedBid.leg_itinerary?.length > 1) {
      return acceptedBid.leg_itinerary;
    } else if (acceptedBid.return_date) {
      const outbound =
        acceptedBid.leg_itinerary?.[0] ||
        buildFlight(
          acceptedBid.departure_airport,
          acceptedBid.arrival_airport,
          acceptedBid.departure_date
        );
      const returnLeg = buildFlight(
        outbound.arrival_airport || acceptedBid.arrival_airport,
        outbound.departure_airport || acceptedBid.departure_airport,
        acceptedBid.return_date
      );
      return [outbound, returnLeg];
    } else if (acceptedBid.leg_itinerary?.length === 1) {
      return acceptedBid.leg_itinerary;
    } else {
      return [
        buildFlight(
          acceptedBid.departure_airport,
          acceptedBid.arrival_airport,
          acceptedBid.departure_date
        ),
      ];
    }
  }, [acceptedBid]);

  // All editable state declarations
  const [crew, setCrew] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [services, setServices] = useState([]);
  const [servicesInitialized, setServicesInitialized] = useState(false);
  const [passengerDrawerOpen, setPassengerDrawerOpen] = useState(false);
  const [editingPassengerIdx, setEditingPassengerIdx] = useState(null);
  const [requestClientPassengers, setRequestClientPassengers] = useState(true);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [crewModalOpen, setCrewModalOpen] = useState(false);
  const [editingCrewIdx, setEditingCrewIdx] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [openSections, setOpenSections] = useState({ trip: true, crew: false, passengers: false, services: false });
  const [completedSections, setCompletedSections] = useState({ trip: false, crew: false, passengers: false, services: false });

  // Reset all editable state when quoteId changes (navigation or refresh)
  useEffect(() => {
    setCrew([]);
    setPassengers([]);
    setServices([]);
    setServicesInitialized(false);
    setOpenSections({ trip: true, crew: false, passengers: false, services: false });
    setCompletedSections({ trip: false, crew: false, passengers: false, services: false });
    setRequestClientPassengers(true);
    setPreviewOpen(false);
    setPassengerDrawerOpen(false);
    setEditingPassengerIdx(null);
    setCrewModalOpen(false);
    setEditingCrewIdx(null);
    setServiceModalOpen(false);
  }, [quoteId]);

  useEffect(() => {
    if (flights.length > 0 && aircraftDetails && !servicesInitialized) {
      setServicesInitialized(true);
    }
  }, [flights, aircraftDetails, servicesInitialized]);

  const handleAddCrewFromModal = (crewData) => {
    if (editingCrewIdx !== null) {
      updateCrew(editingCrewIdx, null, null, crewData);
      setEditingCrewIdx(null);
    } else {
      setCrew((prev) => [...prev, crewData]);
    }
    setCrewModalOpen(false);
  };

  const openEditCrew = (cIdx) => {
    setEditingCrewIdx(cIdx);
    setCrewModalOpen(true);
  };

  const removeCrew = (cIdx) => {
    setCrew((prev) => prev.filter((_, i) => i !== cIdx));
  };

  const updateCrew = (cIdx, field, value, fullData) => {
    setCrew((prev) => {
      const next = [...prev];
      next[cIdx] = fullData || { ...next[cIdx], [field]: value };
      return next;
    });
  };

  const addPassenger = (passengerData) => {
    setPassengers((prev) => {
      const isDuplicate = prev.some(
        (p) => p.fullName === passengerData.fullName && p.dateOfBirth === passengerData.dateOfBirth
      );
      if (isDuplicate) return prev;
      setRequestClientPassengers(false);
      return [...prev, passengerData];
    });
  };

  const updatePassenger = (pIdx, passengerData) => {
    setPassengers((prev) => {
      const next = [...prev];
      next[pIdx] = passengerData;
      return next;
    });
  };

  const removePassenger = (pIdx) => {
    setPassengers((prev) => prev.filter((_, i) => i !== pIdx));
  };

  const handleAddPassengerFromDrawer = (passengerData) => {
    if (editingPassengerIdx !== null) {
      updatePassenger(editingPassengerIdx, passengerData);
      setEditingPassengerIdx(null);
    } else {
      addPassenger(passengerData);
    }
    setPassengerDrawerOpen(false);
  };

  const openEditPassenger = (pIdx) => {
    setEditingPassengerIdx(pIdx);
    setPassengerDrawerOpen(true);
  };

  const addService = (categoryName, details) => {
    setServices((prev) => [...prev, { name: categoryName, details: details || "", fromQuote: false }]);
    setServiceModalOpen(false);
  };

  const removeService = (sIdx) => {
    setServices((prev) => prev.filter((_, i) => i !== sIdx));
  };

  const toggleSection = (key) => {
    setOpenSections((prev) => {
      const isCurrentlyOpen = prev[key];
      return { trip: false, crew: false, passengers: false, services: false, [key]: !isCurrentlyOpen };
    });
  };

  const goToSection = (completedKey, openKey, scrollId) => {
    setCompletedSections((prev) => ({ ...prev, [completedKey]: true }));
    setOpenSections({ trip: false, crew: false, passengers: false, services: false, [openKey]: true });
    setTimeout(() => {
      document.getElementById(scrollId)?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const isLoading = isQuoteLoading || isAircraftLoading;

  return (
    <div className="pb-24">
      <main className="max-w-4xl px-1 py-3">
        <div className="space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              <div className="bg-white rounded-2xl p-5 space-y-4 shadow-md">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-36" />
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm">
                <Skeleton className="h-5 w-40" />
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm">
                <Skeleton className="h-5 w-40" />
              </div>
            </div>
          ) : (
            <>
              {/* Trip Details */}
              <Section icon={RiSuitcase2Line} title="Trip Details" isOpen={openSections.trip} onToggle={() => toggleSection("trip")} completed={completedSections.trip}>
                <div>
                  {/* Client */}
                  <div>
                    <p className="text-lg text-gray-900">{clientName || "—"}</p>
                    {clientCompany && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                        <RiBuilding2Line className="w-3.5 h-3.5" />
                        <span>{clientCompany}</span>
                      </div>
                    )}
                  </div>

                  {/* Tail Number */}
                  <p className="text-xl text-gray-900 tracking-tight mt-3 mb-1">{tailNumber || "—"}</p>

                  {/* Leg Itineraries */}
                  <div >
                    {flights.map((flight, idx) => (
                      <div
                        key={idx}
                        className="flex items-center gap-2 text-sm text-gray-700 border-y border-gray-200 py-2"
                      >
                        <span className="inline-flex items-center gap-1 bg-gray-200 rounded-full px-2 py-1 text-gray-800">
                          <RiFlightTakeoffLine className="w-5 h-5" />
                          {flight.departure_airport}
                        </span>
                        <RiArrowRightLine className="w-4 h-4 text-gray-500" />
                        <span className="inline-flex items-center gap-1 bg-gray-200 rounded-full px-2 py-1 text-gray-800">
                          <RiFlightLandLine className="w-5 h-5" />
                          {flight.arrival_airport}
                        </span>
                        <span className="inline-flex items-center gap-1 text-gray-500 ml-auto">
                          <RiCalendarLine className="w-3 h-3" />
                           {formatUTCToDateTime(flight.departure_date, getAirportTimezone(flight.departure_airport), "MMM D")}
                        </span>
                        <span className="inline-flex items-center gap-1 text-gray-500">
                          <RiTimeLine className="w-3 h-3" />
                          {formatUTCToDateTime(flight.departure_date, getAirportTimezone(flight.departure_airport), "HH:mm", true)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Aircraft / Airport / Passengers */}
                  <div className="space-y-1.5 text-sm text-gray-500 my-3">
                    {aircraftName && (
                      <div className="flex items-center gap-2">
                        <RiPlaneLine className="w-3.5 h-3.5" />
                        <span>{aircraftName}</span>
                      </div>
                    )}
                    {departureAirport && (
                      <div className="flex items-center gap-2">
                        <RiMapPinLine className="w-3.5 h-3.5" />
                        <span>{departureAirport}</span>
                      </div>
                    )}
                    {passengerCount > 0 && (
                      <div className="flex items-center gap-2">
                        <RiTeamLine className="w-3.5 h-3.5" />
                        <span>{passengerCount} passengers</span>
                      </div>
                    )}
                  </div>

                  {/* Next Button */}
                  <button
                    onClick={() => goToSection("trip", "passengers", "passenger-details")}
                    className="w-full flex items-center font-medium justify-center gap-1.5 py-3 border border-primary-50 text-primary-500 text-sm rounded-full transition-colors bg-primary-50"
                  >
                    Next
                    <RiArrowDownSLine className="w-4 h-4" />
                  </button>
                </div>
              </Section>

            {/* Passenger Details */}
            <div id="passenger-details">
              <Section icon={RiTeamLine} title="Passenger Details" isOpen={openSections.passengers} onToggle={() => toggleSection("passengers")} completed={completedSections.passengers}>
                <div className="space-y-4">
                  {passengers.length === 0 ? (
                    <div className="flex flex-col items-center py-6">
                      <svg className="w-24 h-24 mb-3 text-gray-300" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="24" y="12" width="48" height="64" rx="6" stroke="currentColor" strokeWidth="2" fill="white" />
                        <path d="M36 32h24M36 42h24M36 52h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="32" cy="32" r="2" fill="currentColor" />
                        <circle cx="32" cy="42" r="2" fill="currentColor" />
                        <circle cx="32" cy="52" r="2" fill="currentColor" />
                        <circle cx="64" cy="64" r="18" fill="white" stroke="currentColor" strokeWidth="2" />
                        <path d="M58 64h12M64 58v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      <p className="text-sm text-gray-400">No passenger added yet</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {passengers.map((pax, pIdx) => (
                        <div key={pIdx} className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2.5">
                          <span className="text-sm text-gray-900 truncate min-w-0">
                            {pax.fullName || `Passenger ${pIdx + 1}`}
                          </span>
                          <button
                            onClick={() => openEditPassenger(pIdx)}
                            className="text-gray-400 hover:text-gray-600 transition-colors ml-2 flex-shrink-0"
                          >
                            <RiEditLine className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => { setEditingPassengerIdx(null); setPassengerDrawerOpen(true); }}
                    className="w-full flex items-center font-medium justify-center gap-2 py-3 border border-primary-500 text-primary-500 text-sm rounded-full hover:bg-gray-50 transition-colors"
                  >
                    <RiUserAddLine className="w-4 h-4" />
                    Add Passenger
                  </button>

                  {passengers.length === 0 && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={requestClientPassengers}
                        onChange={(e) => setRequestClientPassengers(e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-400"
                      />
                      <span className="text-sm text-gray-600">Request client to complete passenger details</span>
                    </label>
                  )}

                  <div className="flex items-center gap-3 mt-2">
                    <button
                      onClick={() => {
                        setOpenSections({ trip: true, crew: false, passengers: false, services: false });
                        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
                      }}
                      className="flex-1 flex items-center font-medium justify-center gap-1.5 py-3 bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <RiArrowUpSLine className="w-4 h-4" />
                      Back
                    </button>
                    <button
                      onClick={() => goToSection("passengers", "crew", "crew-details")}
                      className="flex-1 flex items-center font-medium justify-center gap-1.5 py-3 border text-sm rounded-full transition-colors bg-primary-50 border-primary-50 text-primary-500"
                    >
                      {requestClientPassengers && passengers.length === 0 ? 'Skip' : 'Next'}
                      <RiArrowDownSLine className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Section>
            </div>

            {/* Crew Details */}
            <div id="crew-details">
              <Section icon={RiNurseLine} title="Crew Details" isOpen={openSections.crew} onToggle={() => toggleSection("crew")} completed={completedSections.crew}>
                <div className="space-y-4">
                  {crew.length > 0 && (
                    <div className="space-y-3">
                      {crew.map((c, cIdx) => (
                        <div key={cIdx} className="border border-gray-200 rounded-lg px-4 py-3 flex items-start justify-between">
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-medium text-gray-900">
                                {c.role}{c.role && c.name ? ":" : ""} {c.name}
                              </span>
                            </div>
                            {c.phone && (
                              <p className="text-xs text-gray-400">{c.phone}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-3">
                            <button
                              onClick={() => openEditCrew(cIdx)}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              <RiEditLine className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeCrew(cIdx)}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <RiCloseLine className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => { setEditingCrewIdx(null); setCrewModalOpen(true); }}
                    className="w-full flex items-center justify-center font-medium gap-2 py-3 border border-primary-500 text-primary-500 text-sm rounded-full hover:bg-gray-50 transition-colors"
                  >
                    <RiUserAddLine className="w-4 h-4" />
                    Add Crew Member
                  </button>

                  <div className="flex items-center gap-3 mt-2">
                    <button
                      onClick={() => {
                        setOpenSections({ trip: false, crew: false, passengers: true, services: false });
                        setTimeout(() => document.getElementById("passenger-details")?.scrollIntoView({ behavior: "smooth" }), 100);
                      }}
                      className="flex-1 flex items-center justify-center font-medium gap-1.5 py-3 bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <RiArrowUpSLine className="w-4 h-4" />
                      Back
                    </button>
                    <button
                      onClick={() => goToSection("crew", "services", "services-section")}
                      className="flex-1 flex items-center font-medium justify-center gap-1.5 py-3 border text-sm rounded-full transition-colors bg-primary-50 border-primary-50 text-primary-500"
                    >
                      Next
                      <RiArrowDownSLine className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Section>
            </div>

            {/* Additional Services */}
            <div id="services-section">
            <Section icon={RiServiceBellLine} title="Additional Services" isOpen={openSections.services} onToggle={() => toggleSection("services")} completed={completedSections.services}>
              <div className="space-y-4">
                <div className="space-y-2">

                  {services.length === 0 ? (
                    <div className="flex flex-col items-center py-6">
                      <svg className="w-24 h-24 mb-3 text-gray-300" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="24" y="12" width="48" height="64" rx="6" stroke="currentColor" strokeWidth="2" fill="white" />
                        <path d="M36 32h24M36 42h24M36 52h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        <circle cx="32" cy="32" r="2" fill="currentColor" />
                        <circle cx="32" cy="42" r="2" fill="currentColor" />
                        <circle cx="32" cy="52" r="2" fill="currentColor" />
                        <circle cx="64" cy="64" r="18" fill="white" stroke="currentColor" strokeWidth="2" />
                        <path d="M58 64h12M64 58v12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      <p className="text-sm text-gray-400">No services added yet</p>
                    </div>
                  ) : (
                    services.map((service, sIdx) => (
                    <div key={sIdx} className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900">{service.name}</p>
                        {service.details && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{service.details}</p>
                        )}
                      </div>
                      {!service.fromQuote && (
                        <button
                          onClick={() => removeService(sIdx)}
                          className="text-gray-400 hover:text-gray-600 transition-colors ml-3 flex-shrink-0"
                        >
                          <RiEditLine className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))
                  )}
                  <button
                    onClick={() => setServiceModalOpen(true)}
                    className="w-full flex items-center justify-center gap-1.5 py-2.5 border border-primary-500 text-primary-500 font-medium text-sm rounded-full transition-colors"
                  >
                    <RiAddLine className="w-4 h-4" />
                    Add Service
                  </button>
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={() => {
                      setOpenSections({ trip: false, crew: true, passengers: false, services: false });
                      setTimeout(() => document.getElementById("crew-details")?.scrollIntoView({ behavior: "smooth" }), 100);
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-gray-50 border border-gray-200 text-gray-800 text-sm font-medium rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <RiArrowUpSLine className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={() => {
                      setCompletedSections((prev) => ({ ...prev, services: true }));
                      setPreviewOpen(true);
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-primary-50 border border-primary-50 text-primary-500 font-medium text-sm rounded-full transition-colors"
                  >
                    <RiEyeLine className="w-4 h-4" />
                    Preview / Edit Trip Sheet
                  </button>
                </div>
              </div>
            </Section>
            </div>
          </>
        )}
        </div>
      </main>
      {serviceModalOpen && (
        <AddServiceModal
          onSelect={addService}
          onClose={() => setServiceModalOpen(false)}
        />
      )}
      {crewModalOpen && (
        <AddCrewModal
          onClose={() => { setCrewModalOpen(false); setEditingCrewIdx(null); }}
          onAdd={handleAddCrewFromModal}
          editingCrew={editingCrewIdx !== null ? crew[editingCrewIdx] : null}
        />
      )}
      <AddPassengerDrawer
        isOpen={passengerDrawerOpen}
        onClose={() => { setPassengerDrawerOpen(false); setEditingPassengerIdx(null); }}
        onAdd={handleAddPassengerFromDrawer}
        onRemove={async () => { if (editingPassengerIdx !== null) { removePassenger(editingPassengerIdx); setEditingPassengerIdx(null); setPassengerDrawerOpen(false); } }}
        editingPassenger={editingPassengerIdx !== null ? passengers[editingPassengerIdx] : null}
        clientId={quoteData?.client?.client_id || quoteData?.client?.id}
      />
      <TripSheetPreviewDrawer
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        quoteData={quoteData}
        acceptedBid={acceptedBid}
        aircraftDetails={aircraftDetails}
        organization={organization}
        flights={flights}
        passengers={passengers}
        crew={crew}
        services={services}
        findAirport={findAirport}
        requestClientPassengers={requestClientPassengers}
      />
    </div>
  );
};

export default TripSheetPage;